import { useEffect, useMemo, useState } from "react";
import OrderStageAccordion from "../../components/OrderStageAccordion";
import VideoSetup from "./VideoSetup/VideoSetup";
import RawFootage from "./RawFootage/RawFootage";
import {
  RequiredRecordingStageActions,
  TOrderVideoStatus,
  TRawFootage,
  TResponseOrderDetails,
  TVideoSetup,
} from "@/types/projectType";
import useOrderVideoParams from "@/hooks/useOrderVideoParams";
import { useGetVideoSetupFilesQuery } from "@/redux/feature/orderManagement/VideoSetup/videoSetupApi";
import { useGetRawFootageQuery } from "@/redux/feature/orderManagement/RawFootage/rawFootageApi";
// import { useGetRawFootageQuery } from "@/redux/feature/orderManagement/RawFootage/rawFootageApi";

import FileIcon from "@/assets/icons/FileIcon.svg";
import DownloadIcon from "@/assets/icons/DownloadIcon.svg";

import { CustomButton } from "@/components/ui/Buttons";
import { useGetFinalVideoScriptQuery } from "@/redux/feature/orderManagement/videoScript/videoScriptApi";
import { handleDownload } from "@/utils/handleDownload";
import { formatFileSize } from "@/utils/formatFileSize";
import { downloadScriptAsPdf } from "@/utils/downloadScriptAsPdf";
import toast from "react-hot-toast";
import { useGetDriveLinkQuery } from "@/redux/feature/driveLink/driveLinkApi";
import { useGlobalContext } from "@/context/GlobalContext";
// import { handleDownload } from "@/utils/handleDownload";

const RecordingStage = ({
  VideoStageStatus,
  disabled,
  requiredActions,
  hideVideoSetup,
  data,
}: {
  requiredActions: RequiredRecordingStageActions[];
  VideoStageStatus?: TOrderVideoStatus;
  disabled: boolean;
  data: TResponseOrderDetails;
  hideVideoSetup: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { orderId, videoId } = useOrderVideoParams();
  const { userData } = useGlobalContext();

  useGetDriveLinkQuery(
    {
      email: userData?.email as string,
      projectId: data?.projectId as string,
      videoId: videoId as string,
      type: "raw_footage",
    },
    {
      skip: !isOpen || !videoId || !data?.projectId || !userData?.email,
    },
  );

  const requiredActionsForCurrentVideo = requiredActions?.find(
    (item) => item?.videoId === videoId,
  )?.adminRequiredActions?.required;

  const recordingPollInterval = useMemo(() => {
    return !VideoStageStatus?.stage_status?.recording_stage?.completed
      ? 5000
      : 0;
  }, [VideoStageStatus?.stage_status?.recording_stage?.completed]);

  // Fetch video setup files with dynamic polling interval
  const { data: videoSetupData } = useGetVideoSetupFilesQuery(
    { orderId, videoId },
    {
      skip: !isOpen || !hideVideoSetup,
      refetchOnMountOrArgChange: true,
      pollingInterval: recordingPollInterval,
    },
  );

  // Fetch video script with dynamic polling interval
  const { data: videoScriptData } = useGetFinalVideoScriptQuery(
    { orderId, videoId },
    {
      skip: !isOpen,
      refetchOnMountOrArgChange: true,
      pollingInterval: recordingPollInterval,
    },
  );

  // const finalScriptVersion =
  //   videoScriptData?.scriptVersions?.slice(-1)[0]?.scriptFile[0];
  const isScriptThere = videoScriptData?.data;
  const scriptContent = videoScriptData?.data;
  const isScriptIsFile = videoScriptData?.data?.format === "file";
  const scriptFile = scriptContent?.files?.[0];

  const HandlerDownloadScript = async () => {
    try {
      if (scriptContent?.format === "text") {
        await downloadScriptAsPdf(
          "Video Script",
          scriptContent?.content ?? "",
        );
      } else {
        await handleDownload(scriptContent.files?.[0]?.live_url);
      }
    } catch {
      toast.error("Could not download the script. Please try again.");
    }
  };

  // Fetch raw footage data with dynamic polling interval
  const { data: rawFootageData, isLoading } = useGetRawFootageQuery(
    { orderId, videoId },
    {
      skip: !isOpen,
      refetchOnMountOrArgChange: true,
      pollingInterval: recordingPollInterval,
    },
  );

  // Automatically open the accordion if the recording stage is active
  useEffect(() => {
    if (!disabled && VideoStageStatus?.stage_status?.recording_stage?.inProgress) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [disabled, VideoStageStatus?.stage_status?.recording_stage?.inProgress]);
  // /video-script/66ceb07a4f209db456a1f538/2
  return (
    <OrderStageAccordion
      completed={VideoStageStatus?.stage_status?.recording_stage?.completed}
      title="Recording stage"
      stageNumber={2}
      disabled={disabled}
      lockedReason="Complete the planning stage first"
      setIsOpen={setIsOpen}
      isOpen={isOpen}
      isProgress={VideoStageStatus?.stage_status?.recording_stage?.inProgress}
      initiallyOpen={
        VideoStageStatus?.stage_status?.recording_stage?.inProgress
      }
    >
      <div className="my-5 space-y-4">
        {isScriptThere && (
          <div className="mx-5 p-3 border border-[#F0EEEE] rounded-lg flex justify-between">
            <div className="flex items-center gap-x-2">
              <FileIcon />
              <div className="space-y-1">
                <h5 className="text-xs font-medium truncate w-[100px] md:w-[300px] leading-4">
                  {isScriptIsFile ? scriptFile?.name : " Video-1 Script"}
                </h5>
                <p className="text-[#959FB2] text-[11px]">
                  {isScriptIsFile && formatFileSize(scriptFile?.size)}
                </p>
              </div>
            </div>
            <CustomButton
              variant="outline"
              color="black"
              fontWeight="500"
              fontSize="12px"
              padding="4px 12px"
              border="1px solid #D3D3D3"
              borderRadius="32px"
              className="leading-[18px] flex items-center gap-x-1"
              onClick={HandlerDownloadScript}
            >
              <DownloadIcon />
              Download
            </CustomButton>
          </div>
        )}

        {/* Render VideoSetup if data is available */}
        {hideVideoSetup && (
          <VideoSetup
            projectId={data?.projectId}
            ActionsRequired={requiredActionsForCurrentVideo}
            isRawFootageExists={
              rawFootageData?.data?.status?.isApproved ? true : false
            }
            videoSetupData={videoSetupData?.data as TVideoSetup}
          />
        )}

        {/* Render RawFootage if data is available */}
        {rawFootageData?.data && (
          <RawFootage
            projectId={data?.projectId}
            ActionsRequired={requiredActionsForCurrentVideo}
            rawFootageData={rawFootageData?.data as TRawFootage}
          />
        )}
      </div>
    </OrderStageAccordion>
  );
};

export default RecordingStage;
